// src/oura/visibility/format.ts
//
// The short date forms this screen prints beside a count. Every date carries
// its year (23 Oct 2026): a line of scans runs across a new year.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function parse(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** "18 Aug 2026". Empty when the stamp is missing or unreadable, never a guess. */
export function dayMonth(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";
}

/** "August", for the merchant's "since August". */
export function monthName(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? MONTHS_LONG[d.getMonth()] : "";
}

/** "16:05", in the reader's time zone, for telling two scans on one day apart. */
export function hourMinute(iso: string | null | undefined): string {
    const d = parse(iso);
    return d ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "";
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** The days on which more than one of these stamps falls. */
export function crowdedDays(isos: (string | null | undefined)[]): Set<string> {
    const seen = new Map<string, number>();
    isos.forEach((iso) => {
        const d = parse(iso);
        if (d) seen.set(dayKey(d), (seen.get(dayKey(d)) ?? 0) + 1);
    });
    return new Set(Array.from(seen.entries()).filter(([, n]) => n > 1).map(([k]) => k));
}

/** Whether two stamps fall on the same day in the reader's time zone. False when either is unreadable. */
export function sameLocalDay(a: string | null | undefined, b: string | null | undefined): boolean {
    const da = parse(a);
    const db = parse(b);
    return !!da && !!db && dayKey(da) === dayKey(db);
}

/** Whether a stamp falls on one of those crowded days. */
export function onCrowdedDay(iso: string | null | undefined, crowded: Set<string>): boolean {
    const d = parse(iso);
    return !!d && crowded.has(dayKey(d));
}

/** "16 Sep", or "16 Sep, 16:05" when another scan shares the day. */
export function scanWhen(iso: string | null | undefined, crowded: Set<string>): string {
    return onCrowdedDay(iso, crowded) ? `${dayMonth(iso)}, ${hourMinute(iso)}` : dayMonth(iso);
}
